
'use client';

import { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Image from 'next/image';
import { Skeleton } from '@/components/ui/skeleton';
import { collection, getDocs, query, where, limit, getCountFromServer, startAfter, orderBy, getDoc, setDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ArtItem, Artist, GameConfig } from '@/types';
import { CheckCircle, XCircle, Loader2, Share2, Archive } from 'lucide-react';
import { cn, toCloudflareImage } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import Link from 'next/link';
import { Label } from '@/components/ui/label';

const MAX_ATTEMPTS = 5;
const GAME_STORAGE_KEY = 'art-vault-game-state';
const ARTIST_CACHE_KEY = 'art-vault-artists-cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

type GameArtwork = Pick<ArtItem, 'id' | 'imageUrls' | 'title' | 'artistNames'>;
type GameData = {
    artwork: GameArtwork;
    gameId: number;
}
type StoredGameState = {
    artwork: GameArtwork;
    gameId: number;
    gameState: 'won' | 'lost' | 'playing';
    previousGuesses: string[];
    date: string; // YYYY-MM-DD
}
type ArtistCache = {
    timestamp: number;
    artists: Artist[];
}

const ptDateYYYYMMDD = () => {
  // produces '2025-08-12' in America/Los_Angeles
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(new Date()); // en-CA -> YYYY-MM-DD
};

const normalize = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();


async function getTodaysArtwork(): Promise<GameData | null> {
  const configRef = doc(db, 'site_content', 'game_config');
  const configSnap = await getDoc(configRef);

  let lastPlayedDocId = '';
  let lastPlayedDate = '';
  if (configSnap.exists()) {
    const c = configSnap.data() as GameConfig;
    lastPlayedDocId = c.lastPlayedDocId ?? '';
    lastPlayedDate = c.lastPlayedDate ?? '';
  }

  const today = ptDateYYYYMMDD();

  const queueCol = collection(db, 'game_queue');
  const ordered = query(queueCol, orderBy('order', 'asc'));

  let docSnapToStartAfter: import('firebase/firestore').QueryDocumentSnapshot | undefined;

  if (lastPlayedDate === today && lastPlayedDocId) {
    // already set for today -> reuse
    const currentQueueDoc = await getDoc(doc(queueCol, lastPlayedDocId));
    if (currentQueueDoc.exists()) {
      const artworkDoc = await getDoc(doc(db, 'artworks', currentQueueDoc.data().artworkId));
      if (!artworkDoc.exists()) return null;
      const data = artworkDoc.data();
      return {
        artwork: { id: artworkDoc.id, imageUrls: data.imageUrls, title: data.title, artistNames: data.artistNames },
        gameId: currentQueueDoc.data().order ?? 0,
      };
    }
  }

  if (lastPlayedDate === today) {
    // fallback if last doc missing
    const first = await getDocs(query(ordered, limit(1)));
    if (first.empty) return null;
    const qdoc = first.docs[0];
    const art = await getDoc(doc(db, 'artworks', qdoc.data().artworkId));
    if (!art.exists()) return null;
    await setDoc(configRef, { lastPlayedDocId: qdoc.id }, { merge: true });
    const d = art.data();
    return { artwork: { id: art.id, imageUrls: d.imageUrls, title: d.title, artistNames: d.artistNames }, gameId: qdoc.data().order ?? 0 };
  }

  // New day: advance to the next doc after lastPlayedDocId (or first if none)
  if (lastPlayedDocId) {
    const prev = await getDoc(doc(queueCol, lastPlayedDocId));
    if (prev.exists()) docSnapToStartAfter = prev as any;
  }

  let nextQ;
  if (docSnapToStartAfter) {
    nextQ = query(ordered, startAfter(docSnapToStartAfter), limit(1));
  } else {
    nextQ = query(ordered, limit(1));
  }

  let nextSnap = await getDocs(nextQ);
  if (nextSnap.empty) {
    // wrapped around
    nextSnap = await getDocs(query(ordered, limit(1)));
    if (nextSnap.empty) return null;
  }

  const nextDoc = nextSnap.docs[0];
  const artworkDoc = await getDoc(doc(db, 'artworks', nextDoc.data().artworkId));
  if (!artworkDoc.exists()) return null;

  await setDoc(configRef, { lastPlayedDocId: nextDoc.id, lastPlayedDate: today, lastPlayedIndex: nextDoc.data().order }, { merge: true });

  const data = artworkDoc.data();
  return {
    artwork: { id: artworkDoc.id, imageUrls: data.imageUrls, title: data.title, artistNames: data.artistNames },
    gameId: nextDoc.data().order ?? 0,
  };
}

async function getCachedArtists(): Promise<Artist[]> {
    if (typeof window === 'undefined') return [];
    try {
        const cachedData = localStorage.getItem(ARTIST_CACHE_KEY);
        if (cachedData) {
            const { timestamp, artists }: ArtistCache = JSON.parse(cachedData);
            if (Date.now() - timestamp < CACHE_DURATION) {
                return artists;
            }
        }
    } catch (e) {
        console.error("Failed to read artist cache from localStorage", e);
    }

    // If cache is invalid or missing, fetch from Firestore
    const artistsCol = collection(db, 'artists');
    const q = query(artistsCol, where('status', '==', 'active'), orderBy('name'));
    const snapshot = await getDocs(q);
    const artists = snapshot.docs.map(doc => ({id: doc.id, ...doc.data()} as Artist));

    // Store the new data in localStorage
    try {
        const cache: ArtistCache = { timestamp: Date.now(), artists };
        localStorage.setItem(ARTIST_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
        console.error("Failed to write artist cache to localStorage", e);
    }

    return artists;
}

const GameCard = memo(function GameCard({ 
    gameState,
    artwork,
    guess,
    setGuess,
    handleGuessSubmit,
    showSuggestions,
    setShowSuggestions,
    filteredArtists,
    previousGuesses,
    activeIndex,
    setActiveIndex,
    isCheckingGuess,
    inputRef,
    children,
}: {
    gameState: string,
    artwork: GameArtwork | null,
    guess: string,
    setGuess: (value: string) => void,
    handleGuessSubmit: () => void,
    showSuggestions: boolean,
    setShowSuggestions: (value: boolean) => void,
    filteredArtists: Artist[],
    previousGuesses: string[],
    activeIndex: number,
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>,
    isCheckingGuess: boolean,
    inputRef: React.RefObject<HTMLInputElement>,
    children: React.ReactNode,
}) {
    if (gameState === 'loading' || !artwork) {
        return (
            <div className="flex flex-col items-center gap-8">
                <Skeleton className="w-full max-w-md aspect-[3/4] rounded-lg" />
                <Skeleton className="h-12 w-full max-w-md" />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-md aspect-[3/4] relative rounded-lg overflow-hidden border shadow-lg bg-muted">
                <Image
                    src={toCloudflareImage(artwork.imageUrls[0], 'public')}
                    alt={`Guess the artist of the artwork "${artwork.title}"`}
                    fill
                    priority
                    placeholder="blur"
                    blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                />
            </div>
            {children}
            {gameState === 'playing' && (
                <div className="w-full max-w-md space-y-4">
                    <div className="relative">
                         <Label htmlFor="artist-guess" className="sr-only">
                            Guess the Artist
                        </Label>
                        {showSuggestions && filteredArtists.length > 0 && (
                            <div
                                role="listbox"
                                id="artist-suggestions"
                                aria-label="Artist suggestions"
                                className="absolute bottom-full mb-2 w-full bg-background border rounded-md shadow-lg z-10 max-h-60 overflow-y-auto"
                            >
                                {filteredArtists.map((artist, index) => (
                                    <button
                                        key={artist.id}
                                        role="option"
                                        id={`option-${artist.id}`}
                                        aria-selected={activeIndex === index}
                                        className={cn(
                                            "w-full text-left p-3 hover:bg-muted",
                                            activeIndex === index && "bg-muted"
                                        )}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setGuess(artist.name);
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        {artist.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Input 
                                ref={inputRef}
                                id="artist-guess"
                                placeholder="Type an artist's name..."
                                value={guess}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setGuess(value);
                                    setShowSuggestions(value.trim().length >= 2);
                                    setActiveIndex(0);
                                }}
                                onBlur={() => {
                                    setTimeout(() => setShowSuggestions(false), 150);
                                }}
                                onFocus={() => {
                                    if(guess.trim().length >= 2) setShowSuggestions(true);
                                }}
                                onKeyDown={(e) => {
                                    if (!showSuggestions || filteredArtists.length === 0) return;
                                    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filteredArtists.length - 1)); }
                                    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
                                    if (e.key === 'Enter')     { e.preventDefault(); const a = filteredArtists[activeIndex]; if (a){ setGuess(a.name); setShowSuggestions(false);} }
                                }}
                                disabled={gameState !== 'playing' || isCheckingGuess}
                                className="h-12 text-md"
                                autoComplete="off"
                                role="combobox"
                                aria-autocomplete="list"
                                aria-controls="artist-suggestions"
                                aria-expanded={showSuggestions && filteredArtists.length > 0}
                                aria-activedescendant={showSuggestions ? `option-${filteredArtists[activeIndex]?.id}` : undefined}
                            />
                            <Button onClick={handleGuessSubmit} disabled={!guess.trim() || isCheckingGuess} className="h-12">
                                {isCheckingGuess && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Submit
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {previousGuesses.length > 0 && (
                <div className="space-y-2 text-center">
                    <h4 className="text-sm font-medium text-muted-foreground">Previous Guesses:</h4>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {previousGuesses.map((pg, i) => (
                            <div key={i} className="text-sm text-red-600 line-through bg-red-100/50 px-2 py-1 rounded-md">
                                {pg}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});
GameCard.displayName = 'GameCard';


export function GamePageContent() {
    const [gameState, setGameState] = useState<'loading' | 'playing' | 'won' | 'lost' | 'error'>('loading');
    const [artwork, setArtwork] = useState<GameArtwork | null>(null);
    const [gameId, setGameId] = useState<number>(0);
    const [allArtists, setAllArtists] = useState<Artist[]>([]);
    const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
    const [guess, setGuess] = useState<string>('');
    const [previousGuesses, setPreviousGuesses] = useState<string[]>([]);
    const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const { toast } = useToast();
    const [activeIndex, setActiveIndex] = useState(0);
    const [isCheckingGuess, setIsCheckingGuess] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    
    const fetchNewGame = useCallback(async (forceNew = false) => {
        setGameState('loading');
        setGuess('');
        setPreviousGuesses([]);
        setFeedback(null);
        
        const todayStr = ptDateYYYYMMDD();

        if (!forceNew) {
            try {
                if (typeof window === 'undefined') return;
                const storedStateJSON = localStorage.getItem(GAME_STORAGE_KEY);
                if (storedStateJSON) {
                    const storedState: StoredGameState = JSON.parse(storedStateJSON);
                    if (storedState.date === todayStr) {
                        setArtwork(storedState.artwork);
                        setGameId(storedState.gameId);
                        setGameState(storedState.gameState);
                        setPreviousGuesses(storedState.previousGuesses);
                        setAttemptsLeft(MAX_ATTEMPTS - storedState.previousGuesses.length);
                         // Fetch artists separately if not stored
                        getCachedArtists().then(setAllArtists);
                        return; // Exit if we successfully loaded today's game
                    }
                }
            } catch (e) {
                console.error("Failed to load game from local storage", e);
                localStorage.removeItem(GAME_STORAGE_KEY);
            }
        } else {
             localStorage.removeItem(GAME_STORAGE_KEY);
        }

        try {
            const [gameData, artistsData] = await Promise.all([getTodaysArtwork(), getCachedArtists()]);
            if (gameData && artistsData.length > 0) {
                setArtwork(gameData.artwork);
                setGameId(gameData.gameId);
                setAllArtists(artistsData);
                setAttemptsLeft(MAX_ATTEMPTS);
                setGameState('playing');
            } else {
                setGameState('error');
            }
        } catch (error) {
            console.error("Failed to start game:", error);
            setGameState('error');
        }
    }, []);

    useEffect(() => {
        fetchNewGame();
    }, [fetchNewGame]);

    const saveStateToStorage = (state: 'playing' | 'won' | 'lost', currentGuesses: string[]) => {
        if (!artwork || typeof window === 'undefined') return;
        const stateToStore: StoredGameState = {
            artwork,
            gameId,
            gameState: state,
            previousGuesses: currentGuesses,
            date: ptDateYYYYMMDD(),
        };
        localStorage.setItem(GAME_STORAGE_KEY, JSON.stringify(stateToStore));
    };


    const handleGuessSubmit = () => {
        if (gameState !== 'playing' || !artwork || !guess || isCheckingGuess) return;
        
        const nGuess = normalize(guess);
        if (previousGuesses.some(g => normalize(g) === nGuess)) {
            toast({ title: 'Already tried that', description: 'Pick a different artist.' });
            return;
        }

        setIsCheckingGuess(true);

        const isCorrect = artwork.artistNames.some(name => normalize(name) === nGuess);

        let newGameState: 'won' | 'lost' | 'playing' = gameState;
        const newPreviousGuesses = [...previousGuesses];
        if (!isCorrect) {
            newPreviousGuesses.push(guess);
        }

        if (isCorrect) {
            setFeedback('correct');
            newGameState = 'won';
        } else {
            setFeedback('incorrect');
            const newAttemptsLeft = MAX_ATTEMPTS - newPreviousGuesses.length;
            setAttemptsLeft(newAttemptsLeft);
            if (newAttemptsLeft <= 0) {
                newGameState = 'lost';
            } else {
                newGameState = 'playing';
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 100);
            }
        }
        
        setPreviousGuesses(newPreviousGuesses);
        setGameState(newGameState);
        saveStateToStorage(newGameState, newPreviousGuesses);

        setGuess('');
        setShowSuggestions(false);
        setIsCheckingGuess(false);
    };
    
    const filteredArtists = useMemo(() => {
        if (!guess) {
            return [];
        }
        const nGuess = normalize(guess);
        const tried = new Set(previousGuesses.map(normalize));
        return allArtists.filter(a => {
            const nName = normalize(a.name);
            return nName.includes(nGuess) && !tried.has(nName);
        }).slice(0, 50);
    }, [guess, allArtists, previousGuesses]);

    const generateShareText = () => {
        let emojis = '';
        const attemptsUsed = previousGuesses.length;

        if (gameState === 'won') {
            emojis = '🔴'.repeat(attemptsUsed) + '🟢' + '⚫'.repeat(MAX_ATTEMPTS - attemptsUsed - 1);
        } else if (gameState === 'lost') {
            emojis = '🔴'.repeat(MAX_ATTEMPTS);
        }
        return `🖼️ Art Vault Guess the Artist #${gameId}  ${emojis} https://artvau.lt/games`;
    };

    const handleShare = async () => {
        const text = generateShareText();
        const shareData: ShareData = {
            title: 'Art Vault — Guess the Artist',
            text,
            url: 'https://artvau.lt/games',
        };

        try {
            // Guard: only attempt native share in a secure, top-level context
            const canUseNativeShare =
            typeof window !== 'undefined' &&
            typeof navigator !== 'undefined' &&
            'share' in navigator &&
            window.isSecureContext &&
            window.top === window.self &&
            document.visibilityState === 'visible';

            if (canUseNativeShare) {
            // Some browsers (Safari) require canShare to return true for the given payload
            // @ts-ignore - canShare is not in older TS lib dom types
            if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) {
                throw new DOMException('Payload not shareable', 'NotAllowedError');
            }
            // @ts-ignore
            await navigator.share(shareData);
            return;
            }

            throw new DOMException('Web Share not supported', 'NotSupportedError');
        } catch (err: any) {
            // If the user cancelled, just stop
            if (err?.name === 'AbortError') return;

            // Fallback 1: clipboard
            try {
            await navigator.clipboard.writeText(text);
            toast({
                title: 'Results copied',
                description: 'Your game results were copied to the clipboard.',
            });
            return;
            } catch {
            // Fallback 2: open a tweet intent
            const tweet = encodeURIComponent(text);
            window.open(`https://twitter.com/intent/tweet?text=${tweet}`, '_blank', 'noopener,noreferrer');
            }
        }
    };

    const GameStatus = () => {
        if (gameState === 'won') {
            return (
                <div className="text-center bg-green-100/50 border border-green-200 p-6 rounded-lg w-full max-w-md">
                    <h2 className="text-2xl font-bold text-green-800">Correct!</h2>
                    <p className="text-green-700">The artist is indeed <span className="font-semibold">{artwork?.artistNames.join(', ')}</span>.</p>
                     <div className="flex flex-col items-center gap-2 justify-center mt-4">
                        <Button variant="outline" onClick={handleShare}><Share2 className="mr-2" aria-hidden="true" /> Share</Button>
                        <Button asChild variant="link" className="text-muted-foreground">
                            <Link href="/games/archive"><Archive className="mr-2" aria-hidden="true"/> View Game Archive</Link>
                        </Button>
                    </div>
                </div>
            )
        }
        if (gameState === 'lost') {
             return (
                <div className="text-center bg-red-100/50 border border-red-200 p-6 rounded-lg w-full max-w-md">
                    <h2 className="text-2xl font-bold text-red-800">Game Over</h2>
                    <p className="text-red-700">The correct artist was <span className="font-semibold">{artwork?.artistNames.join(', ')}</span>.</p>
                     <div className="flex flex-col items-center gap-2 justify-center mt-4">
                        <Button variant="outline" onClick={handleShare}><Share2 className="mr-2" aria-hidden="true" /> Share</Button>
                        <Button asChild variant="link" className="text-muted-foreground">
                            <Link href="/games/archive"><Archive className="mr-2" aria-hidden="true"/> View Game Archive</Link>
                        </Button>
                    </div>
                </div>
            )
        }
        
        return (
             <div className="text-center space-y-2 w-full max-w-md">
                 <p className="text-lg">Attempts left: <span className="font-bold text-primary">{attemptsLeft}</span></p>
                <div className="flex w-full gap-1 h-3">
                    {Array.from({ length: MAX_ATTEMPTS }).map((_, index) => (
                        <div
                            key={index}
                            className={cn(
                                "w-full rounded-sm transition-colors",
                                index < attemptsLeft ? 'bg-primary' : 'bg-secondary'
                            )}
                        />
                    ))}
                </div>
                 <p className="sr-only" aria-live="polite">
                    {feedback === 'incorrect' ? 'Incorrect. Try again.' : feedback === 'correct' ? 'Correct!' : ''}
                </p>
                 {feedback === 'incorrect' && <p className="text-red-700 font-semibold animate-in shake">Incorrect. Try again.</p>}
            </div>
        )
    }
    
    if (gameState === 'error') {
        return (
             <div className="flex flex-col min-h-screen">
                <Header />
                 <main className="flex-1 container mx-auto px-4 py-8">
                     <div className="max-w-2xl mx-auto text-center mt-16">
                        <h1 className="text-4xl font-headline mb-2">Game Not Available</h1>
                        <p className="text-muted-foreground">There is no artwork scheduled for today's game. Please check back later or contact an administrator.</p>
                     </div>
                 </main>
                <Footer />
            </div>
        )
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto text-center mb-8">
                    <h1 className="text-4xl font-headline mb-2">Guess the Artist</h1>
                    <p className="text-muted-foreground">You have {MAX_ATTEMPTS} attempts to guess the correct artist for the artwork shown.</p>
                </div>

                <div className="max-w-md mx-auto space-y-8">
                    <GameCard 
                        gameState={gameState}
                        artwork={artwork}
                        guess={guess}
                        setGuess={setGuess}
                        handleGuessSubmit={handleGuessSubmit}
                        showSuggestions={showSuggestions}
                        setShowSuggestions={setShowSuggestions}
                        filteredArtists={filteredArtists}
                        previousGuesses={previousGuesses}
                        activeIndex={activeIndex}
                        setActiveIndex={setActiveIndex}
                        isCheckingGuess={isCheckingGuess}
                        inputRef={inputRef}
                    >
                         <GameStatus />
                    </GameCard>
                </div>
            </main>
            <Footer />
        </div>
    )
}
